// Google Tag Manager (GTM) only — GA4 fires via GTM tag inside container GTM-MHFWM986.
// GDPR compliant: Consent Mode v2 default is "denied" (set in public/index.html).
// This module ONLY pushes to dataLayer — never calls window.gtag directly and never
// injects the direct gtag/js?id=... script. This avoids the previous double-tracking
// where every page_view and event was sent twice (once by GTM's GA4 tag and once by
// the standalone gtag script), inflating hit counts and breaking bounce rate.

const CONSENT_KEY = "permanina_cookie_consent_v1";

/**
 * Read the current consent object from localStorage.
 * Returns { tech: bool, analytics: bool, ts } — analytics is false by default.
 */
export function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      tech: true,
      analytics: !!parsed.analytics,
      ts: parsed.ts,
    };
  } catch (_) {
    return null;
  }
}

/**
 * Update Google Consent Mode v2 based on user's choice.
 * Called from saveConsent() so GTM's GA4 tag respects the user's decision.
 * Safe to call even if GTM hasn't finished loading: dataLayer.push queues it.
 * After granting analytics, push a follow-up page_view so GTM sees a "granted" hit.
 */
function updateConsentMode({ analytics }) {
  try {
    window.dataLayer = window.dataLayer || [];
    function gtag(){ window.dataLayer.push(arguments); }
    gtag('consent', 'update', {
      analytics_storage: analytics ? 'granted' : 'denied',
      // Ad storage restano 'denied' — non facciamo remarketing/ads personalization
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    if (analytics) {
      // Follow-up page_view così GTM registra un hit con consenso granted
      // (senza questo, tutti gli hit iniziali risultano "denied" e la diagnostica GTM segnala 0% consenso)
      window.dataLayer.push({
        event: 'consent_granted_page_view',
        page_path: window.location.pathname + window.location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  } catch (_) {}
}

/**
 * Persist consent to localStorage. Fires a custom event so components can react.
 */
export function saveConsent({ analytics }) {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({
        accepted: true,
        analytics: !!analytics,
        ts: new Date().toISOString(),
      })
    );
    window.dispatchEvent(new CustomEvent("permanina:consent-changed", { detail: { analytics: !!analytics } }));
  } catch (_) {}
  updateConsentMode({ analytics: !!analytics });
}

/**
 * Track a page view (SPA navigation).
 * Pushes to dataLayer so GTM sees virtual page views on every React Router change.
 * NOT direct-GA4 anymore: GTM's GA4 tag reads the "page_view" dataLayer event.
 */
export function trackPageview(path) {
  if (typeof window === "undefined") return;
  const pagePath = path || window.location.pathname + window.location.search;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'page_view',
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    });
  } catch (_) {}
}

/**
 * Track a custom GA4 event. Pushes to dataLayer only (GTM forwards to GA4).
 * Used for conversion events (sign_up, review_submitted, school_view_engaged, blog_read).
 *
 * @param {string} name  Event name (snake_case, GA4 convention)
 * @param {object} params  Optional event parameters (max 25 keys, string/number values)
 */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...params });
  } catch (_) {}
}

/**
 * Called once at app bootstrap. If consent is already granted from a previous
 * visit, sync Consent Mode v2 accordingly so GTM's GA4 tag fires from the start.
 */
export function initAnalyticsIfConsented() {
  const c = readConsent();
  if (c && c.analytics) {
    updateConsentMode({ analytics: true });
  }
}
